import { Text } from "@shopify/polaris"
import { observer } from "mobx-react"
import React, { useLayoutEffect, useRef, useState } from "react"

import { FormatPriceText } from "@/components"
import stores from "@/stores"

const BANNER_DESIGN_WIDTH = 750

const getBannerScale = (containerWidth: number) => {
  if (containerWidth <= 0) return null
  return Math.min(containerWidth / BANNER_DESIGN_WIDTH, 1)
}

const getFitScale = (innerWidth: number, maxWidth: number) => {
  if (innerWidth <= 0 || maxWidth <= 0) return 1
  return innerWidth > maxWidth ? maxWidth / innerWidth : 1
}

const applyFitScale = (el: HTMLElement | null, maxWidth: number) => {
  if (!el) return
  el.style.transform = "none"
  const scale = getFitScale(el.scrollWidth, maxWidth)
  if (scale < 1) {
    el.style.transform = `scale(${scale})`
    el.style.transformOrigin = "left center"
  } else {
    el.style.transform = ""
    el.style.transformOrigin = ""
  }
}

const STATS_MAX_WIDTH = BANNER_DESIGN_WIDTH - 40
const V6RecommendBanner = () => {
  const { PlanStore, UserInfoStore } = stores
  const user_pkg = PlanStore.config?.user_pkg
  const currencyFormat = UserInfoStore.data.currency_format
  const arr = [
    {
      label: "Retained Value",
      value: user_pkg?.back_in_store_value,
    },
    {
      label: "Upsell Value",
      value: user_pkg?.upsell_value,
    },
    {
      label: "Staff Cost Saved",
      value: user_pkg?.staff_cost_saved,
    },
  ].filter((item) => item.value)
  const length = arr.length

  const recommend_kind = user_pkg?.recommend_plan_kind ?? 0

  const designHeight = 178
  const containerRef = useRef<HTMLDivElement>(null)
  const statsRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState<number | null>(null)

  useLayoutEffect(() => {
    setScale(null)
  }, [recommend_kind])

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    let cancelled = false
    let rafId = 0

    const updateScale = () => {
      const nextScale = getBannerScale(container.getBoundingClientRect().width)
      if (nextScale !== null) {
        setScale(nextScale)
      }
      return nextScale !== null
    }

    const scheduleRetry = (attempt = 0) => {
      if (cancelled || attempt > 10) return
      rafId = requestAnimationFrame(() => {
        if (!updateScale() && !cancelled) {
          scheduleRetry(attempt + 1)
        }
      })
    }

    if (!updateScale()) {
      scheduleRetry()
    }

    const resizeObserver = new ResizeObserver(updateScale)
    resizeObserver.observe(container)
    window.addEventListener("resize", updateScale)

    return () => {
      cancelled = true
      if (rafId) cancelAnimationFrame(rafId)
      resizeObserver.disconnect()
      window.removeEventListener("resize", updateScale)
    }
  }, [recommend_kind, length])

  useLayoutEffect(() => {
    applyFitScale(statsRef.current, STATS_MAX_WIDTH)
  }, [
    scale,
    length,
    recommend_kind,
    user_pkg?.back_in_store_value,
    user_pkg?.upsell_value,
    user_pkg?.staff_cost_saved,
  ])

  const ProfessionalOrEnterprice0 = (data) => (
    <div className="relative">
      <img src={data.img} className="w-full flex" />
    </div>
  )

  const ProfessionalOrEnterprice = (data) => (
    <div className="relative [&_.Polaris-Text--root]:text-white [&_div]:text-[0px]">
      <div className="absolute h-full w-full flex gap-10 items-center">
        <div
          ref={statsRef}
          className="flex gap-2 absolute left-[20px] bottom-5"
        >
          {arr.map((item) => {
            return (
              <div
                key={item.label}
                className="space-y-[2px] flex gap-[6px] items-center rounded-2xl bg-black px-4 py-3"
              >
                <Text variant="headingLg" as="h2">
                  <FormatPriceText
                    value={item.value ?? 0}
                    currency={currencyFormat}
                  />
                </Text>
                <Text variant="bodyLg" as="p">
                  {item.label}
                </Text>
              </div>
            )
          })}
        </div>
      </div>
      <img src={data.img} className="w-full flex" />
    </div>
  )

  const Content = () => {
    if (recommend_kind === 2) {
      return ProfessionalOrEnterprice0({
        img: "https://return-cdn.parcelpanel.com/public/tinified/m/v6_banner_mb_essential.png",
      })
    }
    if (recommend_kind === 3) {
      if (length === 0) {
        return ProfessionalOrEnterprice0({
          img: "https://return-cdn.parcelpanel.com/public/tinified/m/v6_banner_mb_professional.png",
        })
      }
      if (length === 1) {
        return ProfessionalOrEnterprice({
          img: "https://return-cdn.parcelpanel.com/public/tinified/m/v6_banner_mb_50.png",
        })
      }
      if (length === 2) {
        return ProfessionalOrEnterprice({
          img: "https://return-cdn.parcelpanel.com/public/tinified/m/v6_banner_mb_50.png",
        })
      }
      if (length === 3) {
        return ProfessionalOrEnterprice({
          img: "https://return-cdn.parcelpanel.com/public/tinified/m/v6_banner_mb_50_3.png",
        })
      }
    }
    if (recommend_kind === 4) {
      if (length === 0) {
        return ProfessionalOrEnterprice0({
          img: "https://return-cdn.parcelpanel.com/public/tinified/m/v6_banner_mb_enterprise.png",
        })
      }
      if (length === 1) {
        return ProfessionalOrEnterprice({
          img: "https://return-cdn.parcelpanel.com/public/tinified/m/v6_banner_mb_30.png",
        })
      }
      if (length === 2) {
        return ProfessionalOrEnterprice({
          img: "https://return-cdn.parcelpanel.com/public/tinified/m/v6_banner_mb_30.png",
        })
      }
      if (length === 3) {
        return ProfessionalOrEnterprice({
          img: "https://return-cdn.parcelpanel.com/public/tinified/m/v6_banner_mb_30_3.png",
        })
      }
    }
  }

  const content = Content()
  if (!content) return null

  return (
    <div
      ref={containerRef}
      className="w-full overflow-hidden"
      style={{
        aspectRatio: `${BANNER_DESIGN_WIDTH} / ${designHeight}`,
      }}
    >
      <div
        className="origin-top-left"
        style={{
          width: BANNER_DESIGN_WIDTH,
          height: designHeight,
          transform: `scale(${scale ?? 1})`,
          visibility: scale === null ? "hidden" : "visible",
        }}
      >
        {content}
      </div>
    </div>
  )
}

export default observer(V6RecommendBanner)
